import { HelpCircle } from 'lucide-react';
import { useState, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { TOOLTIPS } from '@/lib/tooltips';
import { useTooltipsEnabled } from '@/hooks/useAppSettings';

interface HelpTooltipProps {
  tipKey: string;
  className?: string;
}

export function HelpTooltip({ tipKey, className = '' }: HelpTooltipProps) {
  const enabled = useTooltipsEnabled();
  const text = TOOLTIPS[tipKey];
  const [show, setShow] = useState(false);
  const [coords, setCoords] = useState<{ top: number; left: number; above: boolean }>({ top: 0, left: 0, above: true });
  const iconRef = useRef<HTMLSpanElement>(null);

  const updatePosition = useCallback(() => {
    if (!iconRef.current) return;
    const rect = iconRef.current.getBoundingClientRect();
    const above = rect.top > 140;
    setCoords({
      top: above ? rect.top - 4 : rect.bottom + 4,
      left: Math.max(140, Math.min(rect.left + rect.width / 2, window.innerWidth - 140)),
      above,
    });
  }, []);

  if (!enabled || !text) return null;

  return (
    <span
      ref={iconRef}
      className={`inline-flex items-center ${className}`}
      onMouseEnter={() => { updatePosition(); setShow(true); }}
      onMouseLeave={() => setShow(false)}
      onClick={e => { e.stopPropagation(); updatePosition(); setShow(s => !s); }}
    >
      <HelpCircle className="h-3.5 w-3.5 text-muted-foreground/50 hover:text-muted-foreground cursor-help transition-colors" />
      {show && createPortal(
        <div
          style={{
            position: 'fixed',
            top: coords.above ? undefined : coords.top,
            bottom: coords.above ? `calc(100vh - ${coords.top}px)` : undefined,
            left: coords.left,
            transform: 'translateX(-50%)',
            zIndex: 99999,
          }}
          className="w-64 px-3 py-2 text-xs leading-relaxed text-popover-foreground bg-popover border border-border rounded-md shadow-lg pointer-events-none"
        >
          {text}
        </div>,
        document.body,
      )}
    </span>
  );
}
